/**
 * Pressure Role - Cast Pillar
 *
 * Normaliza input sujo para número, com detecção automática de unidade.
 *
 * @example
 * import { castPressure, castPressureStrict, detectPressureUnit } from '@attrx/role-morphic/pressure/cast';
 *
 * castPressure('1 bar');                      // 100000 (converted to pascals)
 * castPressure('1 bar', 'bar');               // 1 (kept as bar)
 * castPressureStrict('100');                  // 100 (no conversion)
 * detectPressureUnit('14.7 psi');             // 'psi'
 */

import { PRESSURE_UNITS, PRESSURE_ALIASES, type PressureUnit } from "./constants";
import { convertPressure, PRESSURE_BASE } from "./convert";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// CAST
// =============================================================================

/**
 * Faz cast de input para número, com conversão opcional para unidade alvo
 *
 * @param input - Valor de entrada (number, string, etc)
 * @param targetVariant - Unidade alvo (default: pascal)
 * @returns Número ou null se não puder converter
 *
 * @example
 * castPressure('1 bar');            // 100000 (pascals)
 * castPressure('1 bar', 'bar');     // 1
 * castPressure('100');              // 100
 * castPressure(100);                // 100
 * castPressure('invalid');          // null
 */
export function castPressure(
  input: unknown,
  targetVariant: PressureUnit = PRESSURE_BASE,
): number | null {
  // Already a number
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String parsing
  if (typeof input === "string") {
    const parsed = parsePressureString(input);
    if (parsed === null) {
      return null;
    }

    // If unit was detected and differs from target, convert
    if (parsed.unit && parsed.unit !== targetVariant) {
      return convertPressure(parsed.unit, targetVariant, parsed.value);
    }

    return parsed.value;
  }

  return null;
}

/**
 * Faz cast sem conversão (apenas parseia o número)
 *
 * @param input - Valor de entrada
 * @returns Número ou null
 *
 * @example
 * castPressureStrict('100 bar');  // 100 (ignora a unidade)
 * castPressureStrict('100');      // 100
 */
export function castPressureStrict(input: unknown): number | null {
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  if (typeof input === "string") {
    const parsed = parsePressureString(input);
    return parsed?.value ?? null;
  }

  return null;
}

/**
 * Versão safe de castPressure que retorna Result
 */
export function tryCastPressure(
  input: unknown,
  targetVariant: PressureUnit = PRESSURE_BASE,
): Result<number> {
  const result = castPressure(input, targetVariant);
  if (result === null) {
    return { ok: false, error: `Cannot cast "${String(input)}" to pressure` };
  }
  return { ok: true, value: result };
}

// =============================================================================
// DETECT UNIT
// =============================================================================

/**
 * Detecta a unidade de pressão de uma string
 *
 * @param input - String com valor e unidade
 * @returns Unidade detectada ou null
 *
 * @example
 * detectPressureUnit('100 bar');   // 'bar'
 * detectPressureUnit('14.7 psi');  // 'psi'
 * detectPressureUnit('100');       // null
 */
export function detectPressureUnit(input: string): PressureUnit | null {
  const trimmed = input.trim().toLowerCase();

  // Extract unit part (after the number)
  const match = trimmed.match(/^[+-]?[\d.,\s]+\s*(.*)$/);
  if (!match || !match[1]) {
    return null;
  }

  const unitStr = match[1].trim();
  if (!unitStr) {
    return null;
  }

  // Check direct unit names
  if (unitStr in PRESSURE_UNITS) {
    return unitStr as PressureUnit;
  }

  // Check aliases
  if (unitStr in PRESSURE_ALIASES) {
    return PRESSURE_ALIASES[unitStr] as PressureUnit;
  }

  // Check symbols (case-sensitive for some)
  for (const [unit, config] of Object.entries(PRESSURE_UNITS)) {
    if (config.symbol.toLowerCase() === unitStr) {
      return unit as PressureUnit;
    }
  }

  return null;
}

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

interface ParsedPressure {
  value: number;
  unit: PressureUnit | null;
}

/**
 * Parseia string de pressão extraindo valor e unidade
 */
function parsePressureString(input: string): ParsedPressure | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  // Try to extract number and unit
  const match = trimmed.match(/^([+-]?[\d.,\s]+)\s*(.*)$/);
  if (!match) {
    return null;
  }

  const numStr = match[1].replace(/\s/g, "").replace(",", ".");
  const value = parseFloat(numStr);

  if (isNaN(value) || !Number.isFinite(value)) {
    return null;
  }

  const unitStr = match[2]?.trim().toLowerCase() || "";
  let unit: PressureUnit | null = null;

  if (unitStr) {
    // Check aliases first
    if (unitStr in PRESSURE_ALIASES) {
      unit = PRESSURE_ALIASES[unitStr] as PressureUnit;
    }
    // Check direct unit names
    else if (unitStr in PRESSURE_UNITS) {
      unit = unitStr as PressureUnit;
    }
    // Check symbols
    else {
      for (const [u, config] of Object.entries(PRESSURE_UNITS)) {
        if (config.symbol.toLowerCase() === unitStr) {
          unit = u as PressureUnit;
          break;
        }
      }
    }
  }

  return { value, unit };
}
